import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usersAPI } from '../services/api'
import {
  CreditCard,
  AlertCircle,
  CheckCircle,
  Clock,
  ExternalLink,
  Banknote,
  ShieldCheck,
  Zap,
  Info,
  RefreshCw,
  Building2
} from 'lucide-react'

const PayoutSetup = () => {
  const { user, updateUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [stripeStatus, setStripeStatus] = useState('not_connected')
  const [accountDetails, setAccountDetails] = useState(null)
  const [onboardingUrl, setOnboardingUrl] = useState(null)

  useEffect(() => {
    checkStripeStatus()
  }, [])

  const checkStripeStatus = async () => {
    try {
      setLoading(true)
      const response = await usersAPI.getStripeStatus()
      setStripeStatus(response.data.status)
      setAccountDetails(response.data.accountDetails)
    } catch (error) {
      console.error('Error checking Stripe status:', error)
    } finally {
      setLoading(false)
    }
  }

  const initiateStripeOnboarding = async () => {
    try {
      setLoading(true)
      const response = await usersAPI.createStripeConnectAccount()
      setOnboardingUrl(response.data.onboardingUrl)

      // Redirect to Stripe onboarding
      window.location.href = response.data.onboardingUrl
    } catch (error) {
      console.error('Error creating Stripe Connect account:', error)
      alert('Fout bij het opzetten van je uitbetalingsrekening. Probeer het opnieuw.')
    } finally {
      setLoading(false)
    }
  }

  const refreshAccountStatus = async () => {
    try {
      setLoading(true)
      await checkStripeStatus()
      await updateUser()
    } catch (error) {
      console.error('Error refreshing account status:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusInfo = () => {
    switch (stripeStatus) {
      case 'not_connected':
        return {
          icon: <AlertCircle className="w-6 h-6" />,
          color: 'text-amber-500',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          title: 'Bankrekening Koppelen Vereist',
          description: 'Koppel je bankrekening om uitbetalingen te ontvangen van je UGC projecten.'
        }
      case 'pending':
        return {
          icon: <Clock className="w-6 h-6" />,
          color: 'text-[#1D8BF8]',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          title: 'Account In Behandeling',
          description: 'Je accountgegevens worden geverifieerd. Dit kan 1-2 werkdagen duren.'
        }
      case 'active':
        return {
          icon: <CheckCircle className="w-6 h-6" />,
          color: 'text-[#3EB489]',
          bgColor: 'bg-emerald-50',
          borderColor: 'border-emerald-200',
          title: 'Account Actief',
          description: 'Je account is volledig geverifieerd en klaar om uitbetalingen te ontvangen!'
        }
      case 'restricted':
        return {
          icon: <AlertCircle className="w-6 h-6" />,
          color: 'text-red-500',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          title: 'Account Beperkt',
          description: 'Er is aanvullende informatie nodig. Voltooi je accountsetup.'
        }
      default:
        return {
          icon: <Info className="w-6 h-6" />,
          color: 'text-[#94a3b8]',
          bgColor: 'bg-[#F8FAFC]',
          borderColor: 'border-gray-200',
          title: 'Status Onbekend',
          description: 'We controleren je accountstatus...'
        }
    }
  }

  const statusInfo = getStatusInfo()

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl md:text-3xl font-black text-[#0F172A] mb-2 md:mb-4">
          Uitbetalingen & Banking
        </h2>
        <p className="text-sm md:text-base text-[#475569] max-w-2xl mx-auto">
          Koppel je bankrekening om veilig en snel uitbetalingen te ontvangen van je UGC projecten.
          We gebruiken Stripe Connect voor maximale veiligheid.
        </p>
      </div>

      {/* Main Status Card */}
      <div className={`${statusInfo.bgColor} rounded-2xl p-4 md:p-8 border ${statusInfo.borderColor}`}>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
          <div className={`w-12 h-12 md:w-16 md:h-16 ${statusInfo.color} bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm`}>
            {statusInfo.icon}
          </div>

          <div className="flex-1">
            <h3 className="text-xl md:text-2xl font-bold text-[#0F172A] mb-1 md:mb-2">{statusInfo.title}</h3>
            <p className="text-[#475569] mb-4">{statusInfo.description}</p>

            {/* Account Details */}
            {accountDetails && stripeStatus === 'active' && (
              <div className="bg-white rounded-lg p-4 mt-4 border border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-[#94a3b8]">Bankrekeningnummer:</span>
                    <div className="font-semibold text-[#0F172A]">•••• •••• •••• {accountDetails.last4 || '••••'}</div>
                  </div>
                  <div>
                    <span className="text-[#94a3b8]">Bank:</span>
                    <div className="font-semibold text-[#0F172A]">{accountDetails.bankName || 'Niet beschikbaar'}</div>
                  </div>
                  <div>
                    <span className="text-[#94a3b8]">Land:</span>
                    <div className="font-semibold text-[#0F172A]">{accountDetails.country || 'NL'}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3">
            {stripeStatus === 'not_connected' && (
              <button
                onClick={initiateStripeOnboarding}
                disabled={loading}
                className="px-6 py-3 bg-[#3EB489] text-white rounded-2xl font-semibold hover:bg-[#359E78] transition inline-flex items-center shadow-lg shadow-[#3EB489]/20"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Opzetten...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Bankrekening Koppelen
                  </>
                )}
              </button>
            )}

            {(stripeStatus === 'pending' || stripeStatus === 'restricted') && (
              <button
                onClick={() => window.open('https://dashboard.stripe.com/connect/accounts', '_blank')}
                className="px-4 py-2 bg-white border border-blue-200 rounded-xl text-[#1D8BF8] hover:bg-blue-50 transition inline-flex items-center"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Stripe Dashboard
              </button>
            )}

            <button
              onClick={refreshAccountStatus}
              disabled={loading}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-[#0F172A] hover:bg-gray-50 transition inline-flex items-center"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Status Vernieuwen
            </button>
          </div>
        </div>
      </div>

      {/* Information Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* How It Works */}
        <div className="bg-white rounded-2xl p-4 md:p-6 border border-gray-100 shadow-sm">
          <h4 className="text-lg md:text-xl font-bold text-[#0F172A] mb-3 md:mb-4 flex items-center gap-2">
            <Info className="w-5 h-5 text-[#1D8BF8]" />
            Hoe Het Werkt
          </h4>

          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-[#3EB489] rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white">
                1
              </div>
              <div>
                <h5 className="font-semibold text-[#0F172A] mb-1">Veilige Setup</h5>
                <p className="text-sm text-[#475569]">Koppel je bankrekening via Stripe's beveiligde omgeving</p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="w-8 h-8 bg-[#3EB489] rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white">
                2
              </div>
              <div>
                <h5 className="font-semibold text-[#0F172A] mb-1">Automatische Uitbetalingen</h5>
                <p className="text-sm text-[#475569]">Ontvang je verdiende geld direct na project goedkeuring</p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="w-8 h-8 bg-[#3EB489] rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white">
                3
              </div>
              <div>
                <h5 className="font-semibold text-[#0F172A] mb-1">Real-time Tracking</h5>
                <p className="text-sm text-[#475569]">Volg al je uitbetalingen in je dashboard</p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-white rounded-2xl p-4 md:p-6 border border-gray-100 shadow-sm">
          <h4 className="text-lg md:text-xl font-bold text-[#0F172A] mb-3 md:mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#3EB489]" />
            Voordelen
          </h4>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-[#3EB489] flex-shrink-0" />
              <span className="text-sm text-[#475569]">Bank-level beveiliging via Stripe</span>
            </div>

            <div className="flex items-center gap-3">
              <Zap className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <span className="text-sm text-[#475569]">48 uur uitbetaling na goedkeuring</span>
            </div>

            <div className="flex items-center gap-3">
              <Banknote className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <span className="text-sm text-[#475569]">85% van project waarde naar jou</span>
            </div>

            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-[#1D8BF8] flex-shrink-0" />
              <span className="text-sm text-[#475569]">Ondersteunt alle Nederlandse banken</span>
            </div>
          </div>
        </div>
      </div>

      {/* Earnings Overview */}
      <div className="bg-white rounded-2xl p-4 md:p-6 border border-gray-100 shadow-sm">
        <h4 className="text-lg md:text-xl font-bold text-[#0F172A] mb-4 md:mb-6">Verdiensten Overzicht</h4>

        {/* Bedragen als nl-NL valuta (API levert floats zoals 12345.67) en kleiner op mobiel: cellen zijn ~130px op 360px */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
          <div className="text-center p-4 bg-white rounded-xl border border-gray-200 min-w-0">
            <div className="text-xl sm:text-2xl font-bold text-[#3EB489] break-all">
              {Number(user.earnings?.total || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 })}
            </div>
            <div className="text-sm text-[#475569]">Totaal Verdiend</div>
          </div>

          <div className="text-center p-4 bg-white rounded-xl border border-gray-200 min-w-0">
            <div className="text-xl sm:text-2xl font-bold text-[#1D8BF8] break-all">
              {Number(user.earnings?.pending || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 })}
            </div>
            <div className="text-sm text-[#475569]">In Behandeling</div>
          </div>

          <div className="text-center p-4 bg-white rounded-xl border border-gray-200 min-w-0">
            <div className="text-xl sm:text-2xl font-bold text-purple-500">{user.stats?.completedProjects || 0}</div>
            <div className="text-sm text-[#475569]">Voltooide Projecten</div>
          </div>

          <div className="text-center p-4 bg-white rounded-xl border border-gray-200 min-w-0">
            <div className="text-xl sm:text-2xl font-bold text-[#0F172A]">85%</div>
            <div className="text-sm text-[#475569]">Jouw Commissie</div>
          </div>
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
        <div className="flex gap-3">
          <ShieldCheck className="w-5 h-5 text-[#3EB489] flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="font-semibold text-[#3EB489] mb-1">Veiligheid & Privacy</h5>
            <p className="text-sm text-[#475569]">
              Je bankgegevens worden veilig opgeslagen door Stripe, een wereldwijd vertrouwde betalingsverwerker.
              UGC4you heeft geen toegang tot je volledige bankrekeninggegevens.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PayoutSetup
